import { Injectable, Signal, signal } from '@angular/core';
import { ClassType } from '../components/models/class-type.enum';
import { JavaClass } from '../components/models/java-class.class';
import { Method } from '../components/models/method.class';
import { Field } from '../components/models/field.class';
import { BehaviorSubject, Observable } from 'rxjs';
import { FIRST_INDEX, ONE, SECOND_INDEX, ZERO } from '../../../core/constant/number.constant';

@Injectable({
  providedIn: 'root',
})
export class UmlService {
  private _methodList: Method[] = [];
  private _fieldList: Field[] = [];
  private _className: string = '';
  private _classModifier: string[] = [];
  private _attributes: string[] = [];
  private _methods: string[] = [];
  private _isLombockClassGetter: boolean = false;
  private _isLombockClassSetter: boolean = false;
  private _classType: ClassType | undefined;
  private _javaVisibility: string[] = ['public', 'private', 'protected'];
  private _javaClassKeyword: string[] = [
    ...this._javaVisibility,
    'static',
    'abstract',
    'final',
    'non-sealed',
    'sealed',
    'class',
    'interface',
    'record',
  ];
  private _javaFieldKeyword: string[] = [...this._javaVisibility, 'static', 'final', 'transient', 'volatile'];

  javaClass = signal<JavaClass>(new JavaClass());
  javaClass$ = new BehaviorSubject<JavaClass>(new JavaClass());

  public getJavaClass(): Signal<JavaClass> {
    return this.javaClass.asReadonly();
  }

  public getJavaClass$(): Observable<JavaClass> {
    return this.javaClass$.asObservable();
  }

  public transform(javaText: string): void {
    this._reset();
    try {
      const blocks = this._browseLines(javaText.split('\n'));
      this._parseBlocks(blocks);
      this._parseToUml();
    } catch (error) {
      if (error instanceof Error) throw new Error(error.message);
      throw new Error('Une Erreur inconnue est survenue');
    }
    this.javaClass.set(new JavaClass(this._className, this._attributes, this._methods, this._classModifier));
    this.javaClass$.next(new JavaClass(this._className, this._attributes, this._methods, this._classModifier));
  }

  private _browseLines(javaLineArray: string[]): string[] {
    let actualLine = 0;
    try {
      actualLine = this._extractClassName(javaLineArray);
      const blocks = this._createBlocks(javaLineArray, actualLine + ONE);
      return blocks;
    } catch (error) {
      if (error instanceof Error) throw new Error(error.message);
      throw new Error('Une Erreur inconnue est survenue');
    }
  }

  private _extractClassName(javaLineArray: string[]): number {
    for (let i = 0; i < javaLineArray.length; i++) {
      const actualLine = javaLineArray[i];
      if (actualLine.includes('@Getter')) {
        this._isLombockClassGetter = true;
      } else if (actualLine.includes('@Setter')) {
        this._isLombockClassSetter = true;
      } else if (actualLine.includes('class ')) {
        this._classType = ClassType.CLASS;
      } else if (actualLine.includes('record ')) {
        this._classType = ClassType.RECORD;
      } else if (actualLine.includes('interface ')) {
        this._classModifier.push('<<interface>>');
        this._classType = ClassType.INTERFACE;
      }
      if (this._classType != undefined) {
        const words = actualLine.split(' ');
        for (const word of words) {
          if (word === 'abstract') this._classModifier.push('<<abstract>>');
          if (!this._javaClassKeyword.includes(word)) {
            this._className = word;
            return i;
          }
        }
      }
    }

    throw new Error("La classe n'a pas pu être déterminée.");
  }

  private _reset(): void {
    this._className = '';
    this._attributes = [];
    this._methods = [];
    this._methodList = [];
    this._fieldList = [];
    this._classModifier = [];
    this._classType = undefined;
  }

  private _createBlocks(javaLineArray: string[], startingLine: number): string[] {
    const blocks: string[] = [];
    let actualBlock = '';

    for (let i = startingLine; i < javaLineArray.length; i++) {
      actualBlock += ' ' + javaLineArray[i].trim();
      if (this._isActualBlockClosed(actualBlock)) {
        blocks.push(actualBlock.trim());
        actualBlock = '';
      }
    }
    return blocks;
  }

  private _isActualBlockClosed(block: string): boolean {
    if (this._areBlockClosed(block) && (block.endsWith(';') || block.endsWith('}'))) return true;
    return false;
  }

  private _areBlockClosed(block: string): boolean {
    let parenthesis: number = ZERO;
    let bracket: number = ZERO;
    let brace: number = ZERO;
    let isInString: boolean = false;
    for (const character of block) {
      if (character === '"') isInString = !isInString;
      if (isInString) {
        continue;
      }
      if (character === '(') parenthesis += ONE;
      if (character === ')') parenthesis -= ONE;
      if (character === '[') bracket += ONE;
      if (character === ']') bracket -= ONE;
      if (character === '{') brace += ONE;
      if (character === '}') brace -= ONE;
      if (parenthesis < ZERO || bracket < ZERO || (brace < ZERO && block.trim() !== '}')) {
        throw new Error('Les accolades, crochets ou parenthèses ne concordent pas.');
      }
    }
    return parenthesis === ZERO && bracket === ZERO && brace === ZERO;
  }

  private _parseBlocks(blocks: string[]): void {
    for (const block of blocks) {
      this._parseBlock(block);
    }
  }

  private _parseBlock(block: string): void {
    const blockWithoutEqual = block.split('=')[FIRST_INDEX];
    const diamonnedBlock = this._unifyDiamondBlock(block);

    if (blockWithoutEqual.includes('(') && blockWithoutEqual.includes(')')) {
      this._methodList.push(this._parseMethod(diamonnedBlock));
    } else {
      this._fieldList.push(this._parseField(diamonnedBlock));
    }
  }

  private _parseMethod(block: string): Method {
    let name;
    let returnType;

    const words = block.split(' ');
    const wordsWithoutAnnotation = this._removeAnnotations(words);
    const visibility = wordsWithoutAnnotation[FIRST_INDEX];
    for (const word of wordsWithoutAnnotation) {
      if (this._javaFieldKeyword.includes(word)) continue;
      if (returnType === undefined) {
        returnType = word;
      } else if (name === undefined) {
        name = word.substring(FIRST_INDEX, word.indexOf('('));
        continue;
      } else {
        break;
      }
    }
    // cas d'un constructeur
    if (returnType?.startsWith(this._className + '(')) {
      name = this._className;
      returnType = '';
    }

    const argsType: string[] = this._extractMethodArgsTypes(block);
    return new Method(visibility, this._explodeDiamondBlock(returnType as string), name as string, argsType);
  }

  private _extractMethodArgsTypes(block: string): string[] {
    const argsType: string[] = [];
    const blockWithoutStart = block.split('(')[SECOND_INDEX];
    const parameters = blockWithoutStart.split(')')[FIRST_INDEX];
    console.log(parameters);
    for (const parameter of parameters.split(',')) {
      const actualParameter = parameter.split(' ');
      if (actualParameter[FIRST_INDEX].length === ZERO) {
        actualParameter.shift();
      }
      if (actualParameter.length == 2) argsType.push(actualParameter[FIRST_INDEX]);
    }
    return argsType;
  }

  private _parseField(block: string): Field {
    let name;
    let fieldType;

    const words = block.split(' ');

    const isGetter = this._findIsGetter(words);
    const isSetter = this._findIsSetter(words);

    const wordsWithoutAnnotation = this._removeAnnotations(words);

    const visibility = this._findVisibility(wordsWithoutAnnotation);
    const defaultValue = this._findDefaultValue(block);

    for (const word of wordsWithoutAnnotation) {
      if (this._javaFieldKeyword.includes(word)) continue;
      if (fieldType === undefined) {
        fieldType = word;
      } else if (name === undefined) {
        name = word;
        continue;
      } else {
        break;
      }
    }

    const field: Field = new Field(visibility, this._explodeDiamondBlock(fieldType as string), name as string, defaultValue);
    if (isGetter || this._isLombockClassGetter) {
      this._createGetter(field);
    }
    if (isSetter || this._isLombockClassSetter) {
      this._createSetter(field);
    }
    return field;
  }

  private _findDefaultValue(block: string): string {
    if (block.includes('=')) {
      return block.substring(block.indexOf('='), block.length - ONE).trim();
    }
    return '';
  }

  private _findIsGetter(words: string[]): boolean {
    if (words.includes('@Getter')) {
      return true;
    }
    return false;
  }

  private _findIsSetter(words: string[]): boolean {
    if (words.includes('@Setter')) {
      return true;
    }
    return false;
  }

  private _findVisibility(wordsWithoutAnnotation: string[]): string {
    if (this._javaVisibility.includes(wordsWithoutAnnotation[FIRST_INDEX])) {
      return wordsWithoutAnnotation[FIRST_INDEX];
    }
    return 'protected';
  }

  private _parseToUml(): void {
    this._methodList.sort((a, b) => {
      const aValue: number = a.name === this._className ? ONE : ZERO;
      const bValue: number = b.name === this._className ? ONE : ZERO;
      return bValue - aValue;
    });
    for (const method of this._methodList) {
      this._methods.push(method.toUmlString());
    }
    for (const field of this._fieldList) {
      this._attributes.push(field.toUmlString());
    }
  }

  private _createGetter(field: Field): void {
    this._methodList.push(new Method('public', field.fieldType, this._adaptFieldNameForLombock(field.name, 'get'), []));
  }

  private _createSetter(field: Field): void {
    this._methodList.push(new Method('public', 'void', this._adaptFieldNameForLombock(field.name, 'set'), [field.fieldType]));
  }

  private _adaptFieldNameForLombock(name: string, prefix: string): string {
    return prefix + name[FIRST_INDEX].toUpperCase() + name.substring(SECOND_INDEX, name.length);
  }

  private _removeAnnotations(words: string[]): string[] {
    while (words[FIRST_INDEX].startsWith('@')) words.shift();
    return words;
  }

  private _unifyDiamondBlock(block: string): string {
    let newBlock = '';
    let isDiamondOpened: boolean = false;
    for (let character of block) {
      if (isDiamondOpened && character === ' ') {
        character = '§';
      }
      if (character === '<' || character === '>') {
        isDiamondOpened = !isDiamondOpened;
      }
      newBlock += character;
    }
    return newBlock;
  }

  private _explodeDiamondBlock(str: string): string {
    return str.replaceAll('§', ' ');
  }
}
